/**
 * Achievement Row Component
 * Horizontal layout for achievements page list view
 */

import React, { useMemo } from 'react'
import { Box, Tooltip } from '@mui/material'
import { Achievement, TieredAchievement } from '../types/achievementTypes'
import { AchievementIcon } from './AchievementIcon'
import { AchievementProgressBar } from './AchievementProgressBar'
import { getAchievementLevelText } from '../helpers/achievementHelpers'
import { INTERAX_COLORS } from '../../../../constants/colors'

export interface AchievementRowProps {
  achievement: Achievement
  className?: string
}

/**
 * AchievementRow - Horizontal layout for achievements list
 */
export const AchievementRow: React.FC<AchievementRowProps> = ({ achievement, className = '' }) => {
  const level = useMemo(() => {
    if (achievement.type === 'tiered') {
      return (achievement as TieredAchievement).level
    }
    return undefined
  }, [achievement])

  const levelText = useMemo(() => getAchievementLevelText(achievement), [achievement])

  // Use the achievement name and description directly from the transformed data
  const achievementName = achievement.name || achievement.id
  const achievementDescription = achievement.description || ''

  const isCompleted = useMemo(() => {
    if (achievement.type === 'simple') {
      return achievement.isCompleted
    }
    return level !== undefined
  }, [achievement, level])

  const rowContent = (
    <Box
      component="article"
      aria-label={`Achievement: ${achievementName}`}
      className={className}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        padding: 3,
        gap: 3,
        width: '100%',
        transition: 'all 0.3s ease-in-out',
        background: 'linear-gradient(144.63deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)',
        border: `1px solid ${INTERAX_COLORS.white08}`,
        boxShadow: `0px 4px 16px rgba(0, 0, 0, 0.2), inset 0px 1px 0px 1px rgba(255, 255, 255, 0.1)`,
        backdropFilter: 'blur(6px)',
        borderRadius: '16px',
        '&:hover': {
          transform: 'translateX(4px)',
          boxShadow: `0px 4px 16px rgba(255, 171, 64, ${isCompleted ? '0.2' : '0.1'})`
        }
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width: 80,
          height: 80
        }}
      >
        <AchievementIcon level={level} size={80} />
      </Box>

      {/* Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          flex: 1,
          minWidth: 0
        }}
      >
        {/* Name */}
        <Box
          component="h3"
          sx={{
            fontFamily: 'Lexend Deca, sans-serif',
            fontWeight: 700,
            fontSize: '18px',
            lineHeight: '24px',
            margin: 0,
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            background: isCompleted
              ? `linear-gradient(142.35deg, ${INTERAX_COLORS.sun} 22.68%, #FFD700 42.5%, #FFEEAA 55.31%, #FFD14E 69.6%, ${INTERAX_COLORS.sun} 83.62%)`
              : `linear-gradient(128.64deg, #7A96AC 14.45%, #EAEFF3 27.29%, #C2D4E1 36.93%, #FFFFFF 49.55%, #D4DEE5 58.33%, #ABBDC8 70.46%, #BCCAD7 82.6%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textFillColor: 'transparent'
          }}
        >
          {achievementName}
        </Box>

        {/* Description */}
        <Box
          component="p"
          sx={{
            fontFamily: 'DM Sans, sans-serif',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            color: INTERAX_COLORS.typographyFont,
            margin: 0,
            opacity: 0.8
          }}
        >
          {achievementDescription}
        </Box>

        {/* Progress Bar for Tiered Achievements */}
        {achievement.type === 'tiered' && (
          <Box sx={{ mt: 1, maxWidth: '400px' }}>
            <AchievementProgressBar achievement={achievement as TieredAchievement} />
          </Box>
        )}

        {/* Level Text */}
        <Box
          component="p"
          sx={{
            fontFamily: 'DM Sans, sans-serif',
            fontWeight: 500,
            fontSize: '12px',
            lineHeight: '16px',
            color: INTERAX_COLORS.typographyFont,
            margin: 0,
            opacity: 0.7
          }}
        >
          {levelText}
        </Box>
      </Box>
    </Box>
  )

  // Wrap in tooltip if tooltip text is provided (similar to SessionAchievementBadge pattern)
  if (achievement.tooltip) {
    return (
      <Tooltip title={achievement.tooltip} arrow>
        <Box>{rowContent}</Box>
      </Tooltip>
    )
  }

  return rowContent
}
